import React from 'react';
import { Button, Box, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitCampAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { formatDate } from '../../../../../services/functions';

export const CampForm = props => {
  const { data, submitCallback, handleClose, allSeasons } = props;

  const { loading, mutate } = useMutation(submitCampAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    date_from: Yup.date().nullable()
      .required('To pole nie może być puste'),
    date_to: Yup.date().nullable()
      .required('To pole nie może być puste')
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    date_from: data ? data.date_from || null : null,
    date_to: data ? data.date_to || null : null,
    season: data ? data.season || allSeasons[allSeasons.length - 1].value : allSeasons[allSeasons.length - 1].value,
    code_giewartow: data ? data.code_giewartow || '' : '',
    code_mrowki: data ? data.code_mrowki || '' : ''
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano turnus' : 'Dodano nowy turnus', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego turnus');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto turnus ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć turnusa ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa turnusu"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Data od</Typography>
            <SmallFormDateField
              sx={{ flex: '1 1 90%' }}
              name="date_from"
              id="date_from"
              type="text"
              placeholder="date_from"
              value={formik.values.date_from}
              onChange={val => formik.setFieldValue('date_from', formatDate(val))}
              error={formik.touched.date_from && Boolean(formik.errors.date_from)}
              helperText={formik.touched.date_from && formik.errors.date_from}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Data do</Typography>
            <SmallFormDateField
              sx={{ flex: '1 1 90%' }}
              name="date_to"
              id="date_to"
              type="text"
              placeholder="date_to"
              value={formik.values.date_to}
              onChange={val => formik.setFieldValue('date_to', formatDate(val))}
              error={formik.touched.date_to && Boolean(formik.errors.date_to)}
              helperText={formik.touched.date_to && formik.errors.date_to}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Sezon</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="season"
              name="season"
              options={allSeasons}
              value={formik.values.season}
              onChange={formik.handleChange}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Kod Giewartów</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="code_giewartow"
              id="code_giewartow"
              type="text"
              placeholder="Kod Giewartów"
              value={formik.values.code_giewartow}
              onChange={formik.handleChange}
              error={formik.touched.code_giewartow && Boolean(formik.errors.code_giewartow)}
              helperText={formik.touched.code_giewartow && formik.errors.code_giewartow}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Kod Mrówki</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="code_mrowki"
              id="code_mrowki"
              type="text"
              placeholder="Kod Mrówki"
              value={formik.values.code_mrowki}
              onChange={formik.handleChange}
              error={formik.touched.code_mrowki && Boolean(formik.errors.code_mrowki)}
              helperText={formik.touched.code_mrowki && formik.errors.code_mrowki}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
            disabled={loading}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć Zamawiającego?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć ten ośrodek?
      </ConfirmDialog>
    </>

  );
};
